/** A scope's tag-category questions in either mode: the switch, the categories asked (as
 *  chips with their links) and, in edit mode, "Ask about a category". Category names come
 *  from the host's `categories` source; nothing is fetched here.
 *
 *  `facts` (a host that knows them — the operator tab's GET): each chip also says what the
 *  guest form derives for the category (required or not, one or several choices), and a
 *  code the operator's complete list does not have reads as unavailable — an amber chip
 *  with a warning icon and words. */
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha, Box, Chip, Switch, Typography } from '@mui/material';

import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { precheckCategoryActions } from '../links';
import type { PreCheckScope } from '../model';

import { AddRow, ListFailure, NoteRow, ROW_PX, ROW_SX } from './rows';
import { categoryCode, categoryNameOf, type OptionSource, type PrecheckOption, type PrecheckTenant } from './sources';

export interface QuestionsEdit {
  disabled: boolean;
  onEnabled: (on: boolean) => void;
  onRemove: (code: string) => void;
  onAdd: (o: PrecheckOption) => void;
  /** The switch's input id and description (the field slot's label and caption). */
  id: string;
  describedBy: string;
}

export interface QuestionsListProps {
  scope: PreCheckScope;
  enabled: boolean;
  /** The category codes asked. */
  selected: string[];
  /** The party's questions follow the lead booker's. */
  follows: boolean;
  /** Why the questions block Save (switched on with nothing to ask), or null. */
  invalid: string | null;
  /** Null: view mode. */
  edit: QuestionsEdit | null;
  /** The operator's tag categories: names of the codes, the picker's search. */
  categories: OptionSource;
  tenant: PrecheckTenant | null;
  /** What the guest form derives for a category (null: the operator has no such category). */
  facts?: (code: string) => QuestionFacts | null;
}

export interface QuestionFacts {
  required: boolean;
  multiselect: boolean;
}

/** "required · one choice" — a question's derived facts in words. */
export function questionFactsText(f: QuestionFacts): string {
  return `${f.required ? 'required' : 'optional'} · ${f.multiselect ? 'several choices' : 'one choice'}`;
}

export function QuestionsList({ scope, enabled, selected, follows, invalid, edit, categories, tenant, facts }: QuestionsListProps) {
  const who = scope === 'lead' ? 'the lead booker' : 'party members';
  const nameOf = categoryNameOf(categories.list);
  const chipLabel = (code: string) => {
    const name = nameOf(code) ?? code;
    if (!facts) return name;
    const f = facts(code);
    if (!f) {
      return categories.complete ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.dark' }} />
          {name} — not one of this operator’s tag categories
        </Box>
      ) : (
        name
      );
    }
    return (
      <>
        {name}
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
          · {questionFactsText(f)}
        </Box>
      </>
    );
  };
  const unavailable = (code: string) => Boolean(facts && categories.complete && !facts(code));
  return (
    <Box sx={{ display: 'grid', width: '100%', minWidth: 0 }} data-precheck-questions={scope}>
      <Box sx={ROW_SX}>
        {edit ? (
          <Switch
            size="small"
            checked={enabled}
            disabled={edit.disabled}
            onChange={(e) => edit.onEnabled(e.target.checked)}
            inputProps={{ id: edit.id, 'aria-describedby': edit.describedBy, 'aria-label': `Questions from tag categories (${who})` }}
          />
        ) : enabled ? (
          <CheckIcon aria-hidden sx={{ fontSize: 16, color: 'primary.main', mx: 1.25 }} />
        ) : (
          <RemoveIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary', mx: 1.25 }} />
        )}
        <Typography variant="body2">{enabled ? 'Questions from tag categories: on' : 'Questions from tag categories: off'}</Typography>
      </Box>
      {follows && <NoteRow>Same as the lead booker’s.</NoteRow>}
      {enabled && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minHeight: ROW_PX }}>
            {selected.length === 0 && (
              <Typography
                variant="caption"
                color={invalid ? 'error' : 'warning.dark'}
                sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
                data-row-issue={invalid ? 'invalid' : 'warning'}
              >
                <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14 }} />
                Pick at least one category, or switch the questions off.
              </Typography>
            )}
            {selected.map((code) => (
              <ValueActions key={code} chip actions={precheckCategoryActions(code, { tenantId: tenant?.id })}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={chipLabel(code)}
                  data-question-chip={code}
                  {...(unavailable(code) ? { 'data-unavailable': true } : {})}
                  sx={unavailable(code) ? { borderColor: 'warning.main', bgcolor: (t) => alpha(t.palette.warning.main, 0.06) } : undefined}
                  {...(edit && !edit.disabled ? { onDelete: () => edit.onRemove(code) } : {})}
                />
              </ValueActions>
            ))}
          </Box>
          <AddRow>
            {edit && !edit.disabled && (
              <Box sx={{ width: 280 }}>
                <RemoteAutocomplete<PrecheckOption>
                  label="Ask about a category"
                  size="small"
                  fetchOnEmpty
                  value={null}
                  getOptionLabel={(o) => o.name ?? o.code ?? `#${o.id}`}
                  getOptionKey={(o) => o.id}
                  // A source without primary keys (the operator tab's GET: negative ids) names a
                  // category by its code — what a question stores.
                  getOptionMeta={(o) =>
                    o.id > 0
                      ? { name: o.name ?? `#${o.id}`, pk: o.id, tenant: tenant?.code ?? null, extra: [o.code] }
                      : { name: o.name ?? categoryCode(o), pk: categoryCode(o), tenant: tenant?.code ?? null }
                  }
                  queryFn={async (q) => (await categories.search(q)).filter((o) => !selected.includes(categoryCode(o)))}
                  onChange={(v) => {
                    const o = v as PrecheckOption | null;
                    if (o) edit.onAdd(o);
                  }}
                />
              </Box>
            )}
          </AddRow>
          {categories.failed && <ListFailure what="the category names" onRetry={categories.retry} />}
        </>
      )}
    </Box>
  );
}
